<template>
  <div class="distribute-landing">
    <!-- HERO + INLINE FORM -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-grid">
          <div class="hero-text">
            <span class="hero-badge">
              <i class="fas fa-bolt mr-1" />{{ $t('distribute-landing.badge') }}
            </span>
            <h1 class="hero-title">
              <span class="gradient-text">{{ $t('distribute-landing.title') }}</span>
            </h1>
            <p class="hero-subtitle">
              {{ $t('distribute-landing.subtitle') }}
            </p>
            <ul class="hero-bullets">
              <li>
                <i class="fas fa-check" />{{ $t('distribute-landing.bullet-150') }}
              </li>
              <li>
                <i class="fas fa-check" />{{ $t('distribute-landing.bullet-rights') }}
              </li>
              <li>
                <i class="fas fa-check" />{{ $t('distribute-landing.bullet-fast') }}
              </li>
              <li>
                <i class="fas fa-check" />{{ $t('distribute-landing.bullet-no-card') }}
              </li>
            </ul>
            <div
              v-if="totalArtists"
              class="trust-strip"
            >
              <i class="fas fa-users" />
              <span>{{ $t('distribute-landing.trust-strip', { artists: totalArtists }) }}</span>
            </div>
          </div>

          <form
            class="distribute-card"
            novalidate
            @submit.prevent="handleSubmit"
          >
            <h2 class="card-title">
              {{ $t('distribute-landing.form-title') }}
            </h2>
            <p class="card-subtitle">
              {{ $t('distribute-landing.form-subtitle') }}
            </p>

            <!-- DROP ZONE -->
            <div class="form-section">
              <div
                class="dropzone"
                :class="{ 'is-dragging': isDragging, 'has-file': !!audioFile, 'has-error': !!errors.audio }"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
                @click="triggerFilePicker"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept="audio/wav,audio/mp3,audio/mpeg,audio/aac,audio/flac,.wav,.mp3,.aac,.flac"
                  class="dropzone-input"
                  @change="handleFileChange"
                >
                <template v-if="!audioFile">
                  <div class="dropzone-icon">
                    <i class="fas fa-cloud-upload-alt" />
                  </div>
                  <div class="dropzone-title">
                    {{ $t('distribute-landing.dropzone-title') }}
                  </div>
                  <div class="dropzone-hint">
                    {{ $t('distribute-landing.dropzone-hint') }}
                  </div>
                </template>
                <template v-else>
                  <div class="dropzone-file">
                    <i class="fas fa-music" />
                    <div class="dropzone-file-info">
                      <div class="dropzone-file-name">
                        {{ audioFile.name }}
                      </div>
                      <div class="dropzone-file-meta">
                        {{ formatFileSize(audioFile.size) }}
                        <span v-if="audioDuration"> · {{ formatDuration(audioDuration) }}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="dropzone-clear"
                      :aria-label="$t('general.remove')"
                      @click.stop="clearFile"
                    >
                      <i class="fas fa-times" />
                    </button>
                  </div>
                </template>
              </div>
              <p
                v-if="errors.audio"
                class="help is-danger"
              >
                {{ errors.audio }}
              </p>
            </div>

            <!-- TRACK INFO -->
            <div class="form-section form-grid">
              <BaseInput
                v-model="track.title"
                :label="$t('distribute-landing.field-title')"
                :placeholder="$t('distribute-landing.field-title-placeholder')"
                :has-error="!!errors.title"
                :error-message="errors.title"
                left-icon="fas fa-music"
                required
                @blur="validateField('title')"
              />
              <BaseInput
                v-model="track.artist"
                :label="$t('distribute-landing.field-artist')"
                :placeholder="$t('distribute-landing.field-artist-placeholder')"
                :has-error="!!errors.artist"
                :error-message="errors.artist"
                left-icon="fas fa-user-circle"
                required
                @blur="validateField('artist')"
              />
              <div class="field is-full">
                <label class="label">{{ $t('distribute-landing.field-genre') }}</label>
                <div class="control">
                  <div
                    class="select is-fullwidth"
                    :class="{ 'is-danger': errors.genre }"
                  >
                    <select
                      v-model="track.genre"
                      required
                    >
                      <option
                        v-for="g in genreOptions"
                        :key="g"
                        :value="g"
                      >
                        {{ g }}
                      </option>
                    </select>
                  </div>
                </div>
                <p
                  v-if="errors.genre"
                  class="help is-danger"
                >
                  {{ errors.genre }}
                </p>
              </div>
            </div>

            <div
              v-if="errors.global"
              class="error-banner"
            >
              <i class="fas fa-exclamation-circle" /> {{ errors.global }}
            </div>

            <button
              type="submit"
              class="submit-button"
              :disabled="isSubmitting"
            >
              <span
                v-if="!isSubmitting"
                class="submit-button-content"
              >
                <i class="fas fa-rocket mr-2" />
                {{ $t('distribute-landing.submit') }}
              </span>
              <span
                v-else
                class="submit-button-content"
              >
                <i class="fas fa-spinner fa-spin mr-2" />
                {{ submitProgressText }}
              </span>
              <span
                v-if="isSubmitting && submitProgress > 0"
                class="submit-button-progress"
                :style="{ width: `${submitProgress}%` }"
              />
            </button>

            <p
              v-if="!authStore.isAuthenticated"
              class="login-link"
            >
              {{ $t('auth.already-account') }}
              <a
                href="#"
                @click.prevent="openAuthModal('login')"
              >
                {{ $t('auth.sign-in') }}
              </a>
            </p>
          </form>

          <AuthRequiredModal
            v-model="showAuthModal"
            :utm="utmData"
            :initial-mode="authModalMode"
            source="distribute-landing"
            @success="handleAuthSuccess"
          />
        </div>
      </div>
    </section>

    <!-- SEO: How it works -->
    <section class="how-section">
      <div class="container">
        <h2 class="section-title">
          {{ $t('distribute-landing.how-title') }}
        </h2>
        <div class="steps-grid">
          <div class="step-card">
            <div class="step-number">
              1
            </div>
            <i class="fas fa-cloud-upload-alt step-icon" />
            <h3>{{ $t('distribute-landing.how-step-1-title') }}</h3>
            <p>{{ $t('distribute-landing.how-step-1-desc') }}</p>
          </div>
          <div class="step-card">
            <div class="step-number">
              2
            </div>
            <i class="fas fa-magic step-icon" />
            <h3>{{ $t('distribute-landing.how-step-2-title') }}</h3>
            <p>{{ $t('distribute-landing.how-step-2-desc') }}</p>
          </div>
          <div class="step-card">
            <div class="step-number">
              3
            </div>
            <i class="fas fa-globe step-icon" />
            <h3>{{ $t('distribute-landing.how-step-3-title') }}</h3>
            <p>{{ $t('distribute-landing.how-step-3-desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- SEO: DSP grid -->
    <section class="dsps-section">
      <div class="container">
        <h2 class="section-title">
          {{ $t('distribute-landing.dsps-title') }}
        </h2>
        <p class="section-subtitle">
          {{ $t('distribute-landing.dsps-subtitle') }}
        </p>
        <div class="dsps-grid">
          <div
            v-for="dsp in featuredDsps"
            :key="dsp.id"
            class="dsp-card"
            :title="dsp.name"
          >
            <img
              :src="dsp.logo"
              :alt="dsp.name"
              loading="lazy"
            >
          </div>
        </div>
      </div>
    </section>

    <!-- SEO: FAQ -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">
          {{ $t('landing.faq-title') }}
        </h2>
        <div class="faq-grid">
          <details
            v-for="(item, idx) in faqItems"
            :key="idx"
            class="faq-item"
          >
            <summary class="faq-question">
              {{ item.q }}
              <i class="fas fa-chevron-down" />
            </summary>
            <p class="faq-answer">
              {{ item.a }}
            </p>
          </details>
        </div>
      </div>
    </section>

    <!-- SEO: Final CTA -->
    <section class="final-cta-section">
      <div class="container">
        <div class="final-cta">
          <h2>{{ $t('distribute-landing.final-cta-title') }}</h2>
          <p>{{ $t('distribute-landing.final-cta-subtitle') }}</p>
          <button
            class="button is-primary is-large"
            @click="scrollToForm"
          >
            <i class="fas fa-music mr-2" />
            {{ $t('distribute-landing.final-cta-button') }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import BaseInput from '@/components/ui/BaseInput.vue';
import AuthRequiredModal from '@/components/auth/AuthRequiredModal.vue';
import { useAuthStore } from '@/store/auth';
import artistService from '@/services/artistService';
import releaseService from '@/services/releaseService';
import uploadService from '@/services/uploadService';
import publicService from '@/services/publicService';
import userService from '@/services/userService';
import notificationService from '@/services/notificationService';
import generateAutoCover from '@/utils/generateAutoCover';
import { usePageSeo } from '@/composables/usePageSeo';
import { SITE_URL } from '@/config/seo';

import genres from '@/data/genres.js';
import dsps from '@/data/dsps.js';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();

const distributeJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: t('distribute-landing.seo.title'),
  description: t('distribute-landing.seo.description'),
  provider: {
    '@type': 'Organization',
    name: 'Indiefy',
    url: SITE_URL,
  },
  areaServed: 'Worldwide',
  serviceType: 'Music distribution',
  url: `${SITE_URL}/distribute`,
}));

usePageSeo({
  titleKey: 'distribute-landing.seo.title',
  descriptionKey: 'distribute-landing.seo.description',
  keywords: computed(() => t('distribute-landing.seo.keywords')),
  ogType: 'website',
  jsonLd: computed(() => [distributeJsonLd.value]),
});

const fileInput = ref(null);
const audioFile = ref(null);
const audioDuration = ref(0);
const isDragging = ref(false);

const track = ref({
  title: '',
  artist: '',
  genre: 'Pop',
});

const errors = ref({});
const isSubmitting = ref(false);
const submitProgress = ref(0);
const submitProgressText = ref('');
const totalArtists = ref('');
const showAuthModal = ref(false);
const authModalMode = ref('signup');

const genreOptions = computed(() => Object.keys(genres).sort());

const featuredDsps = computed(() => dsps.filter((d) => d.logo).slice(0, 18));

const faqItems = computed(() => [
  { q: t('landing.faq-item5-question'), a: t('landing.faq-item5-answer') },
  { q: t('landing.faq-item2-question'), a: t('landing.faq-item2-answer') },
  { q: t('landing.faq-item3-question'), a: t('landing.faq-item3-answer') },
  { q: t('landing.faq-item4-question'), a: t('landing.faq-item4-answer') },
  { q: t('distribute-landing.faq-cover-question'), a: t('distribute-landing.faq-cover-answer') },
  { q: t('distribute-landing.faq-edit-question'), a: t('distribute-landing.faq-edit-answer') },
]);

const triggerFilePicker = () => {
  if (isSubmitting.value) return;
  fileInput.value?.click();
};

const handleFileChange = (event) => {
  const file = event.target.files?.[0];
  if (file) acceptFile(file);
};

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) acceptFile(file);
};

const acceptFile = (file) => {
  const isAudio = /\.(wav|mp3|flac|aac|m4a)$/i.test(file.name) || file.type.startsWith('audio/');
  if (!isAudio) {
    errors.value.audio = t('distribute-landing.error-audio-type');
    return;
  }
  if (file.size > 200 * 1024 * 1024) {
    errors.value.audio = t('distribute-landing.error-audio-size');
    return;
  }
  errors.value.audio = '';
  audioFile.value = file;

  if (!track.value.title) {
    track.value.title = file.name.replace(/\.[^/.]+$/, '');
  }

  const audio = document.createElement('audio');
  audio.src = URL.createObjectURL(file);
  audio.addEventListener('loadedmetadata', () => {
    audioDuration.value = audio.duration;
    URL.revokeObjectURL(audio.src);
  });
};

const clearFile = () => {
  audioFile.value = null;
  audioDuration.value = 0;
  if (fileInput.value) fileInput.value.value = '';
};

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const scrollToForm = () => {
  document.querySelector('.distribute-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const validateField = (field) => {
  switch (field) {
  case 'title':
    errors.value.title = track.value.title.trim() ? '' : t('distribute-landing.error-required');
    break;
  case 'artist':
    errors.value.artist = track.value.artist.trim() ? '' : t('distribute-landing.error-required');
    break;
  case 'genre':
    errors.value.genre = track.value.genre ? '' : t('distribute-landing.error-required');
    break;
  }
};

const validateTrackFields = () => {
  errors.value = {};
  if (!audioFile.value) {
    errors.value.audio = t('distribute-landing.error-audio-required');
  }
  validateField('title');
  validateField('artist');
  validateField('genre');
  return Object.values(errors.value).every((v) => !v);
};

const focusFirstError = () => {
  const order = ['audio', 'title', 'artist', 'genre'];
  for (const key of order) {
    if (errors.value[key]) {
      const el = document.querySelector('.has-error, .is-danger');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
  }
};

const persistUtm = () => {
  const params = ['utm_campaign', 'utm_source', 'utm_medium'];
  params.forEach((key) => {
    if (route.query[key] && window.localStorage) {
      localStorage.setItem(key, route.query[key]);
    }
  });
};

const getDefaultDsps = () => dsps.map((d) => ({ dsp: d.id }));

const buildReleasePayload = ({ artistId, songFileUrl, songFileName, songFileSize, coverImage, coverImageSmall }) => {
  const releaseDate = new Date();
  releaseDate.setDate(releaseDate.getDate() + 21);

  return {
    title: track.value.title.trim(),
    artist: artistId,
    source: 'web_v3-distribute-landing',
    main_genre: track.value.genre,
    style: track.value.genre,
    release_date: releaseDate.toISOString(),
    original_release_date: releaseDate.toISOString(),
    status: 0,
    language: 'EN',
    label: track.value.artist.trim(),
    pro: 0,
    release_type: 'single',
    upc: '',
    has_lyrics: 0,
    preorder: 0,
    pro_stores: 0,
    content_id: 0,
    cover_image: coverImage,
    cover_image_small: coverImageSmall,
    dsps: getDefaultDsps(),
    songs: [{
      title: track.value.title.trim(),
      artist: track.value.artist.trim(),
      performance_language: 'EN',
      main_genre: track.value.genre,
      style: track.value.genre,
      version: 'Original',
      file_url: songFileUrl,
      file_url_unencoded: songFileUrl,
      file_name: songFileName,
      file_size: songFileSize,
      duration: audioDuration.value || 0,
      isrc: '',
      hasLyrics: false,
      lyrics: '',
      explicit: 0,
      featuring: [],
      performers: [],
      contributors: {
        data: [{
          name: track.value.artist.trim(),
          role: 'Composer',
          percentage: 100,
        }],
      },
    }],
  };
};

const trackUtmForBackend = async () => {
  try {
    const data = { from: 'distribute-landing' };
    if (window.localStorage) {
      data.utm_campaign = localStorage.getItem('utm_campaign') || route.query.utm_campaign || '';
      data.utm_source = localStorage.getItem('utm_source') || route.query.utm_source || '';
      data.utm_medium = localStorage.getItem('utm_medium') || route.query.utm_medium || '';
    }
    await userService.setSubscriptionTrackingData(data);
  } catch (e) {
    console.warn('UTM tracking failed (non-blocking):', e);
  }
};

const getUtmFromStorage = () => {
  const data = {};
  if (!window.localStorage) return data;
  ['utm_campaign', 'utm_source', 'utm_medium'].forEach((key) => {
    const value = localStorage.getItem(key) || route.query[key];
    if (value) data[key] = value;
  });
  return data;
};

const utmData = computed(() => getUtmFromStorage());

const openAuthModal = (mode = 'signup') => {
  authModalMode.value = mode === 'login' ? 'login' : 'signup';
  showAuthModal.value = true;
};

const handleAuthSuccess = async () => {
  showAuthModal.value = false;
  isSubmitting.value = true;
  submitProgress.value = 5;
  submitProgressText.value = t('distribute-landing.progress-account');
  try {
    trackUtmForBackend();
    await runAfterAuthChain();
  } catch (e) {
    console.error('Distribute landing post-auth chain failed:', e);
    errors.value.global = e?.response?.data?.message || e.message || t('distribute-landing.error-generic');
  } finally {
    isSubmitting.value = false;
  }
};

const runAfterAuthChain = async () => {
  try {
    submitProgressText.value = t('distribute-landing.progress-artist');
    submitProgress.value = 25;
    const artist = await artistService.save({
      name: track.value.artist.trim(),
      main_genre: track.value.genre,
      style: track.value.genre,
    });

    submitProgressText.value = t('distribute-landing.progress-uploading');
    submitProgress.value = 35;
    const uploadResp = await uploadService.uploadSong(
      Date.now().toString(),
      audioFile.value,
      (event) => {
        if (event.total > 0) {
          submitProgress.value = 35 + Math.round((event.loaded / event.total) * 45);
        }
      },
    );

    // Backend may report errors (e.g. already_published) in the response body
    if (uploadResp?.error) {
      const uploadError = new Error(uploadResp.error);
      uploadError.code = uploadResp.error;
      throw uploadError;
    }

    submitProgressText.value = t('distribute-landing.progress-cover');
    submitProgress.value = 85;
    const coverFile = await generateAutoCover({
      title: track.value.title.trim(),
      artist: track.value.artist.trim(),
    });
    const coverResp = await uploadService.uploadImage(
      coverFile,
      'distro-covers',
      `${Date.now()}.jpg`,
    );

    submitProgressText.value = t('distribute-landing.progress-creating');
    submitProgress.value = 95;
    const payload = buildReleasePayload({
      artistId: artist._id || artist.id,
      songFileUrl: uploadResp.url,
      songFileName: audioFile.value.name,
      songFileSize: (audioFile.value.size / (1024 * 1024)).toFixed(2),
      coverImage: coverResp.full,
      coverImageSmall: coverResp.small,
    });
    const release = await releaseService.save(payload);

    if (window.gtag) {
      window.gtag('event', 'distribute_landing_complete', {
        event_category: 'Distribute Landing',
        event_label: 'Release Created',
      });
    }
    if (window.fbq) {
      window.fbq('track', 'Lead', { content_name: 'Distribute Landing Release' });
    }
    if (window.rdt) {
      window.rdt('track', 'Lead');
    }

    submitProgress.value = 100;
    router.push({ path: '/distribute/success', query: { id: release._id || release.id } });
  } catch (e) {
    console.error('Distribute landing chain failed:', e);
    const errorCode = e?.response?.data?.error || e?.code;
    errors.value.global = errorCode === 'already_published'
      ? t('upload.already_published')
      : (e?.response?.data?.message || e.message || t('distribute-landing.error-generic'));
    notificationService.error({
      title: t('distribute-landing.error-title'),
      message: errors.value.global,
    });
    submitProgress.value = 0;
    submitProgressText.value = '';
  }
};

const handleSubmit = async () => {
  if (!validateTrackFields()) {
    focusFirstError();
    return;
  }

  if (!authStore.isAuthenticated) {
    openAuthModal();
    return;
  }

  isSubmitting.value = true;
  submitProgress.value = 5;
  submitProgressText.value = t('distribute-landing.progress-account');

  try {
    trackUtmForBackend();
    await runAfterAuthChain();
  } catch (e) {
    console.error('Distribute submit failed:', e);
    errors.value.global = e?.response?.data?.message || e.message || t('distribute-landing.error-generic');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  persistUtm();

  try {
    const info = await publicService.getPublicInfo();
    if (info?.total_users) {
      totalArtists.value = Number(info.total_users).toLocaleString();
    }
  } catch (e) {
    /* non-blocking */
  }
});

defineExpose({
  handleSubmit,
  showAuthModal,
  errors,
  validateTrackFields,
  audioFile,
  track,
  authModalMode,
});
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/breakpoints";

.distribute-landing {
  min-height: 100vh;
  background: var(--color-background, #ffffff);
}

.hero-section {
  position: relative;
  padding: 6rem 0 4rem;
  background: linear-gradient(135deg,
    rgba($indiefy-blue, 0.10) 0%,
    rgba($indiefy-pink, 0.08) 50%,
    rgba($indiefy-lavender, 0.10) 100%);
  overflow: hidden;

  .body--dark & {
    background: linear-gradient(135deg,
      rgba($indiefy-blue, 0.20) 0%,
      rgba($indiefy-pink, 0.18) 50%,
      rgba($indiefy-lavender, 0.20) 100%);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 20%, rgba($indiefy-blue, 0.15) 0%, transparent 40%),
                radial-gradient(circle at 80% 80%, rgba($indiefy-pink, 0.15) 0%, transparent 40%);
    pointer-events: none;
  }

  @media (max-width: $tablet) {
    padding: 4rem 0 3rem;
  }

  @media (max-width: $mobile) {
    padding: 3rem 0 2rem;
  }
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;
  position: relative;
  z-index: 1;

  @media (max-width: $tablet) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: $mobile) {
    gap: 1.5rem;
  }
}

.hero-text {
  padding-top: 2rem;

  @media (max-width: $tablet) {
    padding-top: 0;
    text-align: center;
  }
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  box-shadow: 0 6px 18px rgba($indiefy-pink, 0.25);
}

.hero-title {
  font-family: $family-display;
  font-size: clamp(1.85rem, 4vw, 3.5rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.25rem;
  color: var(--color-text, #1a202c);
  word-break: break-word;

  .gradient-text {
    background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.hero-subtitle {
  font-size: 1.25rem;
  line-height: 1.6;
  color: var(--color-text-light, #4a5568);
  margin-bottom: 2rem;

  @media (max-width: $mobile) {
    font-size: 1.05rem;
    margin-bottom: 1.5rem;
  }
}

.hero-bullets {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
    color: var(--color-text, #1a202c);
    font-weight: 500;

    i {
      color: $success;
      font-size: 1rem;
      flex-shrink: 0;
    }
  }

  @media (max-width: $tablet) {
    display: inline-block;
    text-align: left;
  }
}

.trust-strip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba($indiefy-blue, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.9rem;
  color: var(--color-text, #1a202c);
  max-width: 100%;

  .body--dark & {
    background: rgba(0, 0, 0, 0.3);
    border-color: rgba($indiefy-blue, 0.4);
  }

  i {
    color: $indiefy-blue;
    flex-shrink: 0;
  }

  @media (max-width: $mobile) {
    font-size: 0.8rem;
    padding: 0.4rem 0.75rem;
  }
}

.distribute-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  padding: 2rem;

  .body--dark & {
    background: #1a1a1a;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  }

  // Prevent iOS auto-zoom on focus by ensuring inputs are at least 16px
  :deep(.input),
  :deep(select) {
    font-size: 16px;
  }

  @media (max-width: $tablet) {
    padding: 1.5rem;
  }

  @media (max-width: $mobile) {
    padding: 1.25rem;
    border-radius: 16px;
  }

  @media (max-width: 380px) {
    padding: 1rem;
  }
}

.card-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: var(--color-text, #1a202c);
}

.card-subtitle {
  color: var(--color-text-light, #4a5568);
  margin-bottom: 1.5rem;
}

.form-section {
  margin-bottom: 1.25rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  .is-full {
    grid-column: 1 / -1;
  }

  @media (max-width: $mobile) {
    grid-template-columns: 1fr;
  }
}

.dropzone {
  border: 2px dashed rgba($indiefy-blue, 0.4);
  border-radius: 14px;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  background: rgba($indiefy-blue, 0.04);
  transition: all 0.2s ease;
  position: relative;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: $mobile) {
    padding: 1.5rem 0.75rem;
  }

  &:hover {
    border-color: $indiefy-blue;
    background: rgba($indiefy-blue, 0.08);
    transform: translateY(-2px);
  }

  &.is-dragging {
    border-color: $indiefy-pink;
    background: rgba($indiefy-pink, 0.10);
    transform: scale(1.01);
  }

  &.has-file {
    border-style: solid;
    border-color: $success;
    background: rgba($success, 0.05);
    cursor: default;
  }

  &.has-error {
    border-color: $danger;
    background: rgba($danger, 0.05);
  }
}

.dropzone-input {
  display: none;
}

.dropzone-icon {
  font-size: 2.5rem;
  color: $indiefy-blue;
  margin-bottom: 0.75rem;
}

.dropzone-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--color-text, #1a202c);
  margin-bottom: 0.25rem;

  @media (max-width: $mobile) {
    font-size: 1rem;
  }
}

.dropzone-hint {
  color: var(--color-text-light, #4a5568);
  font-size: 0.9rem;

  @media (max-width: $mobile) {
    font-size: 0.8rem;
  }
}

.dropzone-file {
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: left;

  i.fa-music {
    font-size: 2rem;
    color: $success;
    flex-shrink: 0;
  }
}

.dropzone-file-info {
  flex: 1;
  min-width: 0;
}

.dropzone-file-name {
  font-weight: 600;
  color: var(--color-text, #1a202c);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropzone-file-meta {
  font-size: 0.85rem;
  color: var(--color-text-light, #4a5568);
}

.dropzone-clear {
  background: rgba($danger, 0.1);
  color: $danger;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: $danger;
    color: white;
  }
}

.error-banner {
  background: rgba($danger, 0.1);
  color: $danger;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  margin-top: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.submit-button {
  width: 100%;
  margin-top: 1.5rem;
  padding: 1rem 1.5rem;
  min-height: 56px;
  background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba($indiefy-pink, 0.3);
  transition: all 0.3s ease;

  @media (max-width: $mobile) {
    font-size: 1rem;
    padding: 0.875rem 1rem;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba($indiefy-pink, 0.4);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.85;
  }

  .submit-button-content {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
  }

  .submit-button-progress {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.18);
    transition: width 0.3s ease;
    z-index: 0;
  }
}

.login-link {
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: var(--color-text-light, #4a5568);

  a {
    color: $indiefy-blue;
    font-weight: 600;
    margin-left: 0.25rem;
  }
}

.section-title {
  font-family: $family-display;
  font-size: clamp(1.5rem, 3vw, 2.5rem);
  font-weight: 800;
  text-align: center;
  margin-bottom: 0.75rem;
  color: var(--color-text, #1a202c);
  word-break: break-word;
}

.section-subtitle {
  text-align: center;
  color: var(--color-text-light, #4a5568);
  margin-bottom: 3rem;
  font-size: 1.1rem;

  @media (max-width: $mobile) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
}

.how-section {
  padding: 5rem 0;

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: $tablet) {
    grid-template-columns: 1fr;
  }

  @media (max-width: $mobile) {
    gap: 1.5rem;
    margin-top: 2rem;
  }
}

.step-card {
  background: var(--color-background, #ffffff);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  position: relative;
  transition: transform 0.2s;

  @media (max-width: $mobile) {
    padding: 1.5rem 1.25rem;
  }

  .body--dark & {
    background: #1e1e1e;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    transform: translateY(-4px);
  }

  .step-number {
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
    color: #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
  }

  .step-icon {
    font-size: 2.5rem;
    color: $indiefy-blue;
    margin: 1rem 0;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: var(--color-text, #1a202c);
  }

  p {
    color: var(--color-text-light, #4a5568);
    line-height: 1.6;
  }
}

.dsps-section {
  padding: 5rem 0;
  background: var(--color-background-alt, #f9f9fb);

  .body--dark & {
    background: #141414;
  }

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.dsps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;

  @media (max-width: $mobile) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.75rem;
  }
}

.dsp-card {
  background: var(--color-background, #ffffff);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;

  @media (max-width: $mobile) {
    padding: 1rem;
    height: 64px;
  }

  .body--dark & {
    background: #1e1e1e;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    transform: translateY(-2px);
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.faq-section {
  padding: 5rem 0;

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.faq-grid {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  background: var(--color-background, #ffffff);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

  .body--dark & {
    background: #1e1e1e;
  }

  &[open] .faq-question i {
    transform: rotate(180deg);
  }

  @media (max-width: $mobile) {
    padding: 0.875rem 1rem;
  }
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  list-style: none;
  color: var(--color-text, #1a202c);

  &::-webkit-details-marker {
    display: none;
  }

  i {
    color: $indiefy-blue;
    transition: transform 0.2s;
    flex-shrink: 0;
  }

  @media (max-width: $mobile) {
    font-size: 0.95rem;
  }
}

.faq-answer {
  padding-top: 0.75rem;
  color: var(--color-text-light, #4a5568);
  line-height: 1.6;
}

.final-cta-section {
  padding: 5rem 0;

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.final-cta {
  background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
  border-radius: 24px;
  padding: 4rem 2rem;
  text-align: center;
  color: #ffffff;

  h2 {
    font-family: $family-display;
    font-size: clamp(1.5rem, 3vw, 2.5rem);
    font-weight: 800;
    margin-bottom: 1rem;
    color: #ffffff;
  }

  p {
    font-size: 1.15rem;
    margin-bottom: 2rem;
    opacity: 0.95;
  }

  .button {
    background: #ffffff;
    color: $indiefy-pink;
    border: none;
    font-weight: 700;
    padding: 1rem 2rem;
    min-height: 48px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    }
  }

  @media (max-width: $mobile) {
    padding: 2.5rem 1.25rem;
    border-radius: 18px;

    p {
      font-size: 1rem;
      margin-bottom: 1.5rem;
    }
  }
}
</style>
